import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCaller } from "./useCaller";

function AuthLoading() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground">
      {t("common.loading")}
    </div>
  );
}

export function RequireAuth() {
  const { isAuthenticated, isLoading } = useCaller();
  const location = useLocation();
  if (isLoading) return <AuthLoading />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Outlet />;
}

export function RequireAdmin() {
  const { caller, isLoading, isAuthenticated } = useCaller();
  const location = useLocation();
  if (isLoading) return <AuthLoading />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!caller?.groups.includes("ADMIN")) return <Navigate to="/no-access" replace />;
  return <Outlet />;
}

export function RequireCoachOrAdmin() {
  const { caller, isLoading, isAuthenticated } = useCaller();
  const location = useLocation();
  if (isLoading) return <AuthLoading />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  const allowed = caller?.groups.some((g) => g === "ADMIN" || g === "COACH" || g === "ASSISTANT") ?? false;
  if (!allowed) return <Navigate to="/no-access" replace />;
  return <Outlet />;
}

// ADMIN | COACH only (assistants excluded). Managing assistant accounts is
// a coach/owner action — an assistant must not reach the assistants admin
// page (mirrors the backend authz on /assistants mutations, KAN-180).
export function RequireCoachOrAdminNotAssistant() {
  const { caller, isLoading, isAuthenticated } = useCaller();
  const location = useLocation();
  if (isLoading) return <AuthLoading />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  const allowed = caller?.groups.some((g) => g === "ADMIN" || g === "COACH") ?? false;
  if (!allowed) return <Navigate to="/no-access" replace />;
  return <Outlet />;
}

// ADMIN | COACH | ASSISTANT | OPERATOR. Used for tournaments routes that
// operators also need (list + per-tournament view; the page itself
// hides tabs the operator can't access).
export function RequireSignedIn() {
  const { caller, isLoading, isAuthenticated } = useCaller();
  const location = useLocation();
  if (isLoading) return <AuthLoading />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  const allowed = caller?.groups.some(
    (g) => g === "ADMIN" || g === "COACH" || g === "ASSISTANT" || g === "OPERATOR"
  ) ?? false;
  if (!allowed) return <Navigate to="/no-access" replace />;
  return <Outlet />;
}
